import { useCallback, useEffect, useRef } from 'react';
import type { AssistantTargetKey } from './assistantTargetKeys';
import { useOptionalAssistantUi } from './assistantUiContext';

export function useAssistantTargetCollection(
  targetKey: AssistantTargetKey
): (entityId: string) => (element: HTMLElement | null) => void {
  const assistantUi = useOptionalAssistantUi();
  const registry = assistantUi === null ? null : assistantUi.registry;
  const unregisterByEntityRef = useRef(new Map<string, () => void>());

  useEffect(function unregisterEverythingOnUnmount() {
    const unregisterByEntity = unregisterByEntityRef.current;
    return function unregisterAll() {
      unregisterByEntity.forEach(function unregister(unregisterTarget) {
        unregisterTarget();
      });
      unregisterByEntity.clear();
    };
  }, []);

  return useCallback(
    function buildTargetRef(entityId: string) {
      return function attachTarget(element: HTMLElement | null) {
        const unregisterByEntity = unregisterByEntityRef.current;
        const previousUnregister = unregisterByEntity.get(entityId);
        if (previousUnregister !== undefined) {
          previousUnregister();
          unregisterByEntity.delete(entityId);
        }
        if (element === null || registry === null) {
          return;
        }
        unregisterByEntity.set(entityId, registry.register(targetKey, entityId, element));
      };
    },
    [registry, targetKey]
  );
}
